import { resolveAppApiOrigin } from '../../utils/appApiOrigin';

const apiOrigin = resolveAppApiOrigin();

function lockDownloadUrl(itemId) {
  const origin = apiOrigin || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}/api/storagebox/lock/files/${encodeURIComponent(itemId)}/download`;
}

function messageFromArrayBuffer(data, fallback) {
  if (!(data instanceof ArrayBuffer) || !data.byteLength) return fallback;
  try {
    const parsed = JSON.parse(new TextDecoder().decode(data));
    if (parsed?.message) return parsed.message;
  } catch {
    /* not JSON */
  }
  return fallback;
}

export async function fetchLockCipherBytes(itemId, lockSessionToken, { start, end } = {}) {
  const headers = { 'x-sb-lock-session': lockSessionToken };
  if (start != null && end != null) {
    headers.Range = `bytes=${start}-${end}`;
  }
  const res = await fetch(lockDownloadUrl(itemId), {
    method: 'GET',
    credentials: 'include',
    headers,
  });
  if (res.status === 401) {
    const body = await res.arrayBuffer().catch(() => null);
    const msg = messageFromArrayBuffer(body, 'Lock session expired');
    const err = new Error(msg);
    err.status = 401;
    err.code = 'LOCK_SESSION_EXPIRED';
    throw err;
  }
  if (!res.ok) {
    const body = await res.arrayBuffer().catch(() => null);
    throw new Error(messageFromArrayBuffer(body, `Could not open file (${res.status})`));
  }
  const buffer = await res.arrayBuffer();
  return {
    buffer,
    encIv: res.headers.get('x-sb-enc-iv') || res.headers.get('X-SB-Enc-Iv') || '',
    encAuthTag: res.headers.get('x-sb-enc-tag') || res.headers.get('X-SB-Enc-Tag') || '',
    encFormat: res.headers.get('x-sb-enc-format') || res.headers.get('X-SB-Enc-Format') || '',
    encChunkPlainSize: Number(res.headers.get('x-sb-enc-chunk-plain-size') || res.headers.get('X-SB-Enc-Chunk-Plain-Size') || 0),
    mimeType: res.headers.get('x-sb-plain-mime') || res.headers.get('X-SB-Plain-Mime') || '',
    plainSizeBytes: Number(res.headers.get('x-sb-plain-size') || res.headers.get('X-SB-Plain-Size') || 0),
  };
}

/** Stream ciphertext into memory in fixed network chunks (no axios size cap). */
export async function fetchLockCipherFullBuffer(itemId, lockSessionToken, onProgress) {
  const headers = { 'x-sb-lock-session': lockSessionToken };
  const res = await fetch(lockDownloadUrl(itemId), {
    method: 'GET',
    credentials: 'include',
    headers,
  });
  if (res.status === 401) {
    const body = await res.arrayBuffer().catch(() => null);
    const msg = messageFromArrayBuffer(body, 'Lock session expired');
    const err = new Error(msg);
    err.status = 401;
    err.code = 'LOCK_SESSION_EXPIRED';
    throw err;
  }
  if (!res.ok) {
    const body = await res.arrayBuffer().catch(() => null);
    throw new Error(messageFromArrayBuffer(body, `Could not open file (${res.status})`));
  }
  const meta = {
    encIv: res.headers.get('x-sb-enc-iv') || '',
    encAuthTag: res.headers.get('x-sb-enc-tag') || '',
    encFormat: res.headers.get('x-sb-enc-format') || '',
    encChunkPlainSize: Number(res.headers.get('x-sb-enc-chunk-plain-size') || 0),
    mimeType: res.headers.get('x-sb-plain-mime') || '',
    plainSizeBytes: Number(res.headers.get('x-sb-plain-size') || 0),
  };
  const reader = res.body?.getReader();
  if (!reader) {
    const buffer = await res.arrayBuffer();
    return { buffer, ...meta };
  }
  const parts = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(value);
    received += value.byteLength;
    onProgress?.(received);
  }
  const buffer = new Uint8Array(received);
  let offset = 0;
  parts.forEach((chunk) => {
    buffer.set(chunk, offset);
    offset += chunk.byteLength;
  });
  return { buffer: buffer.buffer, ...meta };
}
